/* Novi Sens theme — theme.js (no dependencies) */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Header: mega panels + mobile drawer ---------- */
  document.querySelectorAll('[data-nav-item]').forEach((item) => {
    const trigger = item.querySelector('[data-nav-trigger]');
    if (!trigger || !item.querySelector('.mega')) return;
    let timer;
    const open = () => { clearTimeout(timer); closeAll(item); item.classList.add('is-open'); trigger.setAttribute('aria-expanded', 'true'); };
    const close = () => { timer = setTimeout(() => { item.classList.remove('is-open'); trigger.setAttribute('aria-expanded', 'false'); }, 120); };
    item.addEventListener('mouseenter', open);
    item.addEventListener('mouseleave', close);
    trigger.addEventListener('click', (e) => {
      if (item.classList.contains('is-open')) return; // second click follows the link
      e.preventDefault(); open();
    });
    item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
  });
  function closeAll(except) {
    document.querySelectorAll('[data-nav-item].is-open').forEach((el) => {
      if (el !== except) { el.classList.remove('is-open'); el.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'false'); }
    });
  }
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeAll(); toggleDrawer(false); } });

  const drawer = document.querySelector('[data-drawer]');
  const menuBtn = document.querySelector('[data-menu-toggle]');
  function toggleDrawer(force) {
    if (!drawer || !menuBtn) return;
    const open = typeof force === 'boolean' ? force : !drawer.classList.contains('is-open');
    drawer.classList.toggle('is-open', open);
    menuBtn.setAttribute('aria-expanded', String(open));
    menuBtn.querySelector('[data-menu-label]').textContent = open ? 'Close' : 'Menu';
    document.documentElement.style.overflow = open ? 'hidden' : '';
  }
  menuBtn?.addEventListener('click', () => toggleDrawer());

  /* ---------- Hero: full-bleed media settles into a rounded tile as you scroll ---------- */
  document.querySelectorAll('[data-hero-settle]').forEach((frame) => {
    if (reduceMotion) { frame.style.setProperty('--p', 1); return; }
    let ticking = false;
    const update = () => {
      const p = Math.min(1, Math.max(0, window.scrollY / (window.innerHeight * 0.35)));
      frame.style.setProperty('--p', p.toFixed(3));
      ticking = false;
    };
    window.addEventListener('scroll', () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } }, { passive: true });
    update();
  });

  /* ---------- Product rails ---------- */
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    const track = rail.querySelector('.rail__track');
    const prev = rail.querySelector('[data-rail-prev]');
    const next = rail.querySelector('[data-rail-next]');
    if (!track) return;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 20;
    const sync = () => {
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });

  /* ---------- Ecosystem layers (tabs + stacked circles stay in sync) ---------- */
  document.querySelectorAll('[data-eco]').forEach((eco) => {
    const tabs = [...eco.querySelectorAll('[role="tab"]')];
    const circles = [...eco.querySelectorAll('[data-eco-circle]')];
    const select = (i, focus) => {
      tabs.forEach((t, j) => {
        const on = i === j;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
        if (on && focus) t.focus();
      });
      circles.forEach((c, j) => c.classList.toggle('is-active', i === j));
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(i));
      t.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') select((i + 1) % tabs.length, true);
        if (e.key === 'ArrowLeft') select((i - 1 + tabs.length) % tabs.length, true);
      });
    });
    circles.forEach((c, i) => c.addEventListener('click', () => select(i)));
  });

  /* ---------- Product: variants, quantity, gallery dots ---------- */
  document.querySelectorAll('[data-product]').forEach((root) => {
    const dataEl = root.querySelector('[data-product-json]');
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const form = root.querySelector('[data-product-form]');
    const idInput = form?.querySelector('input[name="id"]');
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare]');
    const addBtn = root.querySelector('[data-add]');
    const money = (cents) => new Intl.NumberFormat((root.dataset.currency || 'USD') === 'USD' ? 'en-US' : (document.documentElement.lang || 'en'), { style: 'currency', currency: root.dataset.currency || 'USD', currencyDisplay: 'narrowSymbol' }).format(cents / 100);

    const unitEl = root.querySelector('[data-unit-price]');
    const saveEl = root.querySelector('[data-save]');
    const per = (v) => v.price / Math.max(1, v.units || 1);
    const basePer = Math.max(...product.variants.map(per));
    const savePct = (v) => Math.round((1 - per(v) / basePer) * 100);
    // add "Save x%" to option pills when a single option drives the pack size
    if (root.querySelectorAll('[data-option]').length === 1) {
      root.querySelectorAll('[data-option] input').forEach((input) => {
        const v = product.variants.find((x) => x.options[0] === input.value);
        const pct = v ? savePct(v) : 0;
        if (pct > 0) input.nextElementSibling.insertAdjacentHTML('beforeend', ` <span class="pill-save">Save ${pct}%</span>`);
      });
    }
    const showUnit = (variant) => {
      if (!unitEl || !variant) return;
      unitEl.textContent = money(per(variant));
      const pct = savePct(variant);
      if (saveEl) { saveEl.hidden = pct <= 0; saveEl.textContent = `Save ${pct}%`; }
    };
    const current = () => [...root.querySelectorAll('[data-option] input:checked')].map((i) => i.value);
    const update = () => {
      const selected = current();
      const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      // mark combinations that don't exist / are sold out
      root.querySelectorAll('[data-option]').forEach((group, gi) => {
        group.querySelectorAll('input').forEach((input) => {
          const test = [...selected]; test[gi] = input.value;
          const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
      if (!variant) { if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; } return; }
      if (idInput) idInput.value = variant.id;
      showUnit(variant);
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) { compareEl.textContent = variant.compare_at_price > variant.price ? money(variant.compare_at_price) : ''; }
      if (addBtn && !addBtn.dataset.locked) {
        addBtn.disabled = !variant.available;
        addBtn.textContent = variant.available ? addBtn.dataset.label : 'Sold out';
      }
      if (variant.featured_media) {
        const target = root.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        target?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };
    root.querySelectorAll('[data-option] input').forEach((i) => i.addEventListener('change', update));
    if (root.querySelector('[data-option]')) update();
    else showUnit(product.variants[0]);

    root.querySelectorAll('[data-qty]').forEach((qty) => {
      const input = qty.querySelector('input');
      qty.querySelector('[data-qty-minus]').addEventListener('click', () => { input.value = Math.max(1, (+input.value || 1) - 1); });
      qty.querySelector('[data-qty-plus]').addEventListener('click', () => { input.value = (+input.value || 1) + 1; });
    });

    const gallery = root.querySelector('.gallery');
    const dots = root.querySelectorAll('.gallery__dots span');
    if (gallery && dots.length) {
      gallery.addEventListener('scroll', () => {
        const i = Math.round(gallery.scrollLeft / (gallery.firstElementChild.getBoundingClientRect().width + 10));
        dots.forEach((d, j) => d.classList.toggle('is-active', i === j));
      }, { passive: true });
    }
  });

  /* ---------- Add to cart (AJAX, falls back to normal form post) ---------- */
  const toast = document.querySelector('.toast');
  let toastTimer;
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || !window.fetch) return;
    e.preventDefault();
    const btn = form.querySelector('[data-add]');
    btn && (btn.disabled = true);
    try {
      const res = await fetch(window.routes.cart_add_url + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!res.ok) { const err = await res.json(); throw new Error(err.description || 'Could not add this item.'); }
      const cart = await (await fetch(window.routes.cart_url + '.js')).json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.dataset.count = cart.item_count; });
      if (toast) {
        toast.querySelector('.toast__text').textContent = 'Added to your cart';
        toast.hidden = false;
        requestAnimationFrame(() => toast.classList.add('is-visible'));
        clearTimeout(toastTimer);
        toastTimer = setTimeout(() => { toast.classList.remove('is-visible'); setTimeout(() => (toast.hidden = true), 300); }, 4000);
      }
    } catch (err) {
      if (toast) { toast.querySelector('.toast__text').textContent = err.message; toast.hidden = false; requestAnimationFrame(() => toast.classList.add('is-visible')); }
    } finally {
      btn && (btn.disabled = false);
    }
  });
})();
